import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import SEO from "@/components/SEO";
import { useLang } from "@/i18n";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * One-click newsletter unsubscribe. The welcome/newsletter emails link here
 * with a per-subscriber token; the page confirms removal instantly.
 */
export default function Unsubscribe() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState(token ? "working" : "invalid");
  const { t } = useLang();

  useEffect(() => {
    if (!token) return;
    // Idempotent either way: a used/unknown token still shows the success state
    // so the page never reveals whether an address was subscribed.
    axios
      .post(`${API}/newsletter/unsubscribe`, { token })
      .then(() => setState("done"))
      .catch(() => setState("done"));
  }, [token]);

  return (
    <>
      <SEO title="Unsubscribe | Farmsons" description="Unsubscribe from the Farmsons newsletter." />
      <section data-testid="unsubscribe-page" className="min-h-screen bg-[#ffffff] flex items-center justify-center px-6 pt-24 pb-16">
        <div className="w-full max-w-md text-center border border-[#1d3a1f]/10 rounded-2xl p-10 card-soft bg-[#fdfbf5]">
          {state === "working" && (
            <>
              <Loader2 size={40} className="mx-auto text-[#d3a53a] animate-spin" />
              <h1 className="mt-6 font-display font-bold text-2xl text-[#1d3a1f]">{t("unsub.title")}</h1>
              <p data-testid="unsubscribe-status" className="mt-3 text-[#5f6b57]">{t("unsub.processing")}</p>
            </>
          )}
          {state === "done" && (
            <>
              <div className="w-16 h-16 mx-auto rounded-full bg-[#d3a53a] flex items-center justify-center">
                <CheckCircle2 className="text-[#fdfbf5]" size={32} />
              </div>
              <h1 className="mt-6 font-display font-bold text-2xl text-[#1d3a1f]">{t("unsub.success")}</h1>
              <p data-testid="unsubscribe-status" className="mt-3 text-[#5f6b57]">{t("unsub.successBody")}</p>
            </>
          )}
          {state === "invalid" && (
            <>
              <div className="w-16 h-16 mx-auto rounded-full bg-[#c0492f]/15 flex items-center justify-center">
                <XCircle className="text-[#a23b26]" size={32} />
              </div>
              <h1 className="mt-6 font-display font-bold text-2xl text-[#1d3a1f]">{t("unsub.invalid")}</h1>
              <p data-testid="unsubscribe-status" className="mt-3 text-[#5f6b57]">{t("unsub.invalidBody")}</p>
            </>
          )}
          <Link to="/" data-testid="unsubscribe-home" className="mt-8 inline-flex items-center gap-2 text-sm font-bold text-[#d3a53a] hover:text-[#1d3a1f] transition-colors">
            {t("unsub.home")}
          </Link>
        </div>
      </section>
    </>
  );
}
